import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";
import { ictPrimitives } from "./ict-primitives.js";

export const swingIct: WikiTopic = {
  slug: "ict-swing",
  title: "ICT swing",
  summary: "Sweeps, structure shifts, gaps, order blocks, and the composite setup they feed.",
  group: "strategies",
  horizon: "swing",
  strategyKey: "swing_ict",
  sections: [
    {
      heading: "When it runs",
      code: "ict_sweep_* / ict_mss_* / ict_fvg_* / ict_ob_* / ict_long / ict_short",
      blocks: [
        p(
          <>
            Once a session at <Datum>20:30</Datum>, last in the evening chain: adjustments are
            materialised, then factor scoring at <Datum>20:00</Datum>, then the rank and technical
            signals at <Datum>20:15</Datum>. It reads adjusted daily bars, so it must not start
            before that session's adjustments exist.
          </>,
        ),
        p(
          <>
            It asks for <Datum>{QUOTED.SWING_DAILY_LOOKBACK_SESSIONS}</Datum> daily sessions per
            symbol and folds the weekly bars out of that same array rather than querying them.{" "}
            <Datum>{QUOTED.SWING_DAILY_LOOKBACK_SESSIONS}</Datum> sessions is about eighty weeks,
            and a second read would be a second source of truth for the same numbers.
          </>,
        ),
        p(
          <>
            The daily array includes this session's own completed bar. That is the opposite of the
            hourly rule, which hands a strategy bars up to yesterday because CPR is defined on the
            previous session's range. None of that applies to a strategy that evaluates after the
            close, and copying the hourly rule here would build a strategy that looks at yesterday.
          </>,
        ),
        p(
          <>
            Two hard gates stop a symbol before anything else runs: fewer than{" "}
            <Datum>{QUOTED.SWING_MIN_DAILY_BARS}</Datum> daily bars, or an ATR that is null or zero.
            A symbol whose last <Datum>{QUOTED.SWING_ATR_PERIOD}</Datum> sessions have no range did
            not trade, and that is a gate rather than a throw because every caller counts a throw as
            a strategy failure.
          </>,
        ),
      ],
    },
    {
      heading: "Everything is an ATR multiple",
      blocks: [
        p(
          <>
            The reference this was ported from tunes its parameters as percentages for intraday
            crypto: a <Datum>0.05%</Datum> minimum gap, a <Datum>0.3%</Datum> minimum order-block
            move, a <Datum>0.5%</Datum> entry proximity, a <Datum>0.2%</Datum> stop buffer. A{" "}
            <Datum>0.05%</Datum> daily gap is <Datum>2.5 paise</Datum> on a fifty-rupee name and{" "}
            <Datum>₹2.50</Datum> on a five-thousand-rupee one, and only one of those is noise. ATR
            is scale-free, and every magnitude in this repo already reports in ATR units.
          </>,
        ),
        table(
          "Swing ICT parameters",
          ["Parameter", "Value"],
          [
            ["SWING_ATR_PERIOD", <Datum key="p">{QUOTED.SWING_ATR_PERIOD} sessions</Datum>],
            ["SWING_FVG_MIN_ATR", <Datum key="fvg">{QUOTED.SWING_FVG_MIN_ATR} ATR</Datum>],
            ["SWING_OB_MIN_MOVE_ATR", <Datum key="ob">{QUOTED.SWING_OB_MIN_MOVE_ATR} ATR</Datum>],
            ["SWING_OB_FORWARD_BARS", <Datum key="fwd">{QUOTED.SWING_OB_FORWARD_BARS} bars</Datum>],
            [
              "SWING_ZONE_PROXIMITY_ATR",
              <Datum key="z">{QUOTED.SWING_ZONE_PROXIMITY_ATR} ATR</Datum>,
            ],
            ["SWING_STOP_BUFFER_ATR", <Datum key="sb">{QUOTED.SWING_STOP_BUFFER_ATR} ATR</Datum>],
            [
              "SWING_DISPLACEMENT_THRESHOLD",
              <Datum key="d">{QUOTED.SWING_DISPLACEMENT_THRESHOLD} body over range</Datum>,
            ],
            ["SWING_MIN_DAILY_BARS", <Datum key="md">{QUOTED.SWING_MIN_DAILY_BARS} bars</Datum>],
            ["SWING_MIN_WEEKLY_BARS", <Datum key="mw">{QUOTED.SWING_MIN_WEEKLY_BARS} bars</Datum>],
            [
              "SWING_DAILY_LOOKBACK",
              <Datum key="dl">{QUOTED.SWING_DAILY_LOOKBACK} bars either side</Datum>,
            ],
            [
              "SWING_WEEKLY_LOOKBACK",
              <Datum key="wl">{QUOTED.SWING_WEEKLY_LOOKBACK} bars either side</Datum>,
            ],
            [
              "SWING_DAILY_LOOKBACK_SESSIONS",
              <Datum key="dls">{QUOTED.SWING_DAILY_LOOKBACK_SESSIONS} sessions</Datum>,
            ],
          ],
        ),
        note(
          <>
            One exception proves the rule. <Datum>SWING_EQUAL_LEVEL_THRESHOLD</Datum> stays a
            percentage, at <Datum>{QUOTED.SWING_EQUAL_LEVEL_THRESHOLD}</Datum> of the pair's own
            average, because it compares two prices on one instrument and the scale cancels. The
            weekly lookback is also the odd one out at <Datum>{QUOTED.SWING_WEEKLY_LOOKBACK}</Datum>{" "}
            rather than <Datum>{QUOTED.SWING_DAILY_LOOKBACK}</Datum>: eighty weekly bars are few,
            and a lookback of three needs seven bars to produce one swing point.
          </>,
        ),
        note(
          <>
            These are starting values, not measured ones. The validation harness measures them over
            history and reports; it does not set them.
          </>,
        ),
      ],
    },
    ictPrimitives,
    {
      heading: "The composite ladder",
      code: "ict_long / ict_short",
      blocks: [
        p(
          <>
            Five rungs after the two hard gates, each named so a rejection is countable rather than
            an empty result. A ladder that hands back no rows can say "no composite" and cannot say
            which rung stopped it, so the evaluation carries the gate's name back.
          </>,
        ),
        ul([
          <>
            <Datum>bias</Datum>: the weekly bias must not be neutral. Neutral is not a soft answer,
            it is the strictest gate here, and it is the main reason the swing composite fires on a
            fraction of what the retired hourly setup did.
          </>,
          <>
            <Datum>structure</Datum>: a sweep or a shift pointing the wrong way is a veto, and no
            aligned event at all is a miss. Two different rejections, one gate name.
          </>,
          <>
            <Datum>zone</Datum>: a live, aligned, near-enough fair value gap or order block must
            exist. Unfilled gaps and unmitigated blocks only, within{" "}
            <Datum>{QUOTED.SWING_ZONE_PROXIMITY_ATR}</Datum> ATR of the nearer edge, and the newest
            candidate wins a tie.
          </>,
          <>
            <Datum>trigger</Datum>: the last full hourly bucket of the session must displace into
            the zone in the right direction. Without it the entry price is the daily close, which is
            the one price of the day nobody transacts at.
          </>,
          <>
            <Datum>levels</Datum>: a stop and a target must both resolve. A stop whose anchor sits
            on the wrong side of the entry returns null rather than a number, because{" "}
            <Datum>10</Datum> of <Datum>216</Datum> live rows on <Datum>2026-08-28</Datum> were
            longs with stops above their entries.
          </>,
        ]),
        p(
          <>
            Magnitude on a composite is the reward-to-risk ratio, both legs measured as a percentage
            of the entry. That is the number a threshold would eventually gate on, so that is the
            number the distribution has to hold. Two of the reference's rules are deliberately
            absent: a minimum confluence count, which the structure-and-zone requirement already
            implies, and its risk and reward-to-risk rejections, which are unmeasured crypto
            intraday constants. Those numbers are computed and stored instead of enforced.
          </>,
        ),
      ],
    },
    {
      heading: "What ships and what does not",
      blocks: [
        p(
          <>
            The <Datum>ict_fvg_*</Datum> and <Datum>ict_ob_*</Datum> rows are off by default. The
            gap rows fired on <Datum>81%</Datum> of <Datum>8,840</Datum> measured symbol-sessions
            and the order-block rows on <Datum>94%</Datum>. The first live session wrote{" "}
            <Datum>10,323</Datum> of the pair against <Datum>370</Datum> sweeps and shifts and no
            composite at all.
          </>,
        ),
        p(
          <>
            A row that fires nine sessions in ten reports where price is standing rather than that
            something happened, and a feed of them buries the few that did. Nothing is lost by
            leaving them off: both are inputs the composite already consumes, and its payload
            records the zone it chose in <Datum>zoneTop</Datum>, <Datum>zoneBottom</Datum>, and a
            confluence sentence naming the kind. The validation harness turns them back on, because
            a firing rate cannot be measured from rows nobody wrote.
          </>,
        ),
        p(
          <>
            Every type is <Datum>ict_</Datum>-prefixed. The unique index on the signals table covers
            exchange, symbol, signal type, and session date, and it does not include family, so an
            unprefixed <Datum>mss_bullish</Datum> would collide with the retired hourly one and
            produce a single win rate over two different definitions.
          </>,
        ),
        note(
          <>
            The strategy name on every row is <Datum>swing_ict</Datum>, not <Datum>ict</Datum>, for
            the same reason. Severity comes from the shared table rather than a hard-coded value,
            which returns <Datum>info</Datum> for every swing type until the thresholds are filled
            from a measured distribution.
          </>,
        ),
      ],
    },
  ],
};
